import { type ReactElement } from 'react';

import type { EditableDraft } from '@/features/patient-record/hooks/useEditableDraft';

import { DirtyFooter } from './DirtyFooter';

interface DraftFooterProps<T> {
  readonly form: EditableDraft<T>;
  /** Card name for the unsaved-edit log line + the manual footer. */
  readonly label?: string;
  /**
   * Override the saving flag — for cards whose mutation is owned by a PARENT (e.g. the physical-chart
   * card), where `form.saving` isn't the real in-flight state. Defaults to `form.saving`.
   */
  readonly saving?: boolean;
}

/**
 * PTV2-1327 — the footer for a record card backed by `useEditableDraft`:
 *   • autosave ON  → **nothing** (no per-section chrome). The global Save button in the record header
 *     / pedigree toolbar reflects the aggregate Unsaved / Saving… / Saved state.
 *   • autosave OFF → the explicit {@link DirtyFooter} (Save / Discard), only while dirty.
 *
 * `register={false}` on the DirtyFooter because the card already registers with the dirty registry
 * through the primitive.
 */
export function DraftFooter<T>({ form, label, saving }: DraftFooterProps<T>): ReactElement | null {
  if (form.autosaveActive) return null;
  if (!form.isDirty) return null;
  return (
    <DirtyFooter
      register={false}
      {...(label !== undefined ? { label } : {})}
      saving={saving ?? form.saving}
      onSave={form.save}
      onDiscard={form.discard}
    />
  );
}
